import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { defer, of, race, shareReplay, tap, throwError } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import {
  SharedEmailProvidersActions,
  SharedEmailProvidersSelectors,
} from '@core/email-providers-data';
import type { SharedEmailProvider } from '@core/email-providers-types';
import { SharedEmailProviders } from '@core/email-providers-types';
import { SharedMailboxesAnalytics } from '@core/mailbox-logic';
import { fetchIfNotLoaded } from '@core/ngrx-legacy';
import { WindowRef } from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

import { throwUnsupportedProvider } from '../helpers/unsupported-provider-error';

const noop = () => void 0;

@Injectable({ providedIn: 'root' })
export class SharedEmailProvidersFacade {
  private readonly sharedMailboxesAnalytics = inject(SharedMailboxesAnalytics);
  private readonly hasWorkflowAutomations = injectDevFlag(DevFlag.WorkflowAutomations);

  public store = inject(Store);
  public actions$ = inject(Actions);
  public windowRef = inject(WindowRef);

  public providers$ = defer(() =>
    this.store.pipe(
      fetchIfNotLoaded(this.providersMeta$, () => this.fetchCollection()),
      select(SharedEmailProvidersSelectors.entities()),
    ),
  ).pipe(
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  public providersMeta$ = this.store.pipe(select(SharedEmailProvidersSelectors.meta())).pipe(
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  public getProvider(id: number) {
    return this.store.pipe(
      fetchIfNotLoaded(this.providersMeta$, () => this.fetchCollection()),
      select(SharedEmailProvidersSelectors.byId(id)),
      filterBoolean,
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );
  }

  public getProviderOrThrow(id: number) {
    return this.store.pipe(
      fetchIfNotLoaded(this.providersMeta$, () => this.fetchCollection()),
      select(SharedEmailProvidersSelectors.byId(id)),
      switchMap(p => (p === null ? throwError(() => new Error('Missing provider')) : of(p))),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );
  }

  public getProviderMeta(id: number) {
    return this.store.pipe(
      fetchIfNotLoaded(this.providersMeta$, () => this.fetchCollection()),
      select(SharedEmailProvidersSelectors.meta(id)),
      filterBoolean,
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );
  }

  public fetchCollection() {
    this.store.dispatch(SharedEmailProvidersActions.fetchCollection());
  }

  public create(
    provider: SharedEmailProviders,
    ok: (provider: SharedEmailProvider) => void = noop,
    fail: () => void = noop,
  ) {
    this.store.dispatch(
      SharedEmailProvidersActions.createOnePessimistic({
        payload: provider,
      }),
    );

    const success$ = this.actions$.pipe(
      ofType(SharedEmailProvidersActions.createOnePessimisticSuccess),
      take(1),
      tap(({ payload }) => ok(payload.data)),
    );

    const failed$ = this.actions$.pipe(
      ofType(SharedEmailProvidersActions.createOnePessimisticFailed),
      take(1),
      tap(({ payload }) => fail()),
    );

    return race(success$, failed$);
  }

  public archiveMailboxWithAutomatedAction(
    payload: {
      from: number;
      to: { type: string; id: number | null };
    },
    ok: (provider: SharedEmailProvider) => void = noop,
    fail: () => void = noop,
  ) {
    this.sharedMailboxesAnalytics.trackArchiveWithAutomatedAction();

    this.store.dispatch(
      this.hasWorkflowAutomations
        ? SharedEmailProvidersActions.archiveWithAutomationActionMigration({ payload })
        : SharedEmailProvidersActions.archiveWithAutomatedActionMigration({ payload }),
    );

    const success$ = this.actions$.pipe(
      ofType(
        this.hasWorkflowAutomations
          ? SharedEmailProvidersActions.archiveWithAutomationActionMigrationSuccess
          : SharedEmailProvidersActions.archiveWithAutomatedActionMigrationSuccess,
      ),
      tap(({ payload }) => ok(payload)),
    );

    const failed$ = this.actions$.pipe(
      ofType(
        this.hasWorkflowAutomations
          ? SharedEmailProvidersActions.archiveWithAutomationActionMigrationFailed
          : SharedEmailProvidersActions.archiveWithAutomatedActionMigrationFailed,
      ),
      tap(() => fail()),
    );

    return race(success$, failed$);
  }

  public delete(id: number) {
    this.store.dispatch(SharedEmailProvidersActions.removeOneOptimistic({ payload: { data: id } }));
  }

  public archive(provider: SharedEmailProvider) {
    this.sharedMailboxesAnalytics.trackArchive();
    this.store.dispatch(
      SharedEmailProvidersActions.archiveOneOptimistic({
        payload: { data: provider, correlationId: generateUuid() },
      }),
    );
  }

  public update(
    data: Pick<SharedEmailProvider, 'id' | 'name' | 'signature' | 'visibility'>,
    ok: (provider: SharedEmailProvider) => void = noop,
    fail: () => void = noop,
  ) {
    this.store.dispatch(
      SharedEmailProvidersActions.updateOnePessimistic({
        payload: {
          data,
        },
      }),
    );

    const success$ = this.actions$.pipe(
      ofType(SharedEmailProvidersActions.updateOnePessimisticSuccess),
      take(1),
      tap(({ payload }) => ok(payload.data)),
    );

    const failed$ = this.actions$.pipe(
      ofType(SharedEmailProvidersActions.updateOnePessimisticFailed),
      take(1),
      tap(({ payload }) => fail()),
    );

    return race(success$, failed$);
  }

  public connectCustomMailbox(
    provider: SharedEmailProvider,
    ok: (provider: SharedEmailProvider) => void = noop,
    fail: () => void = noop,
  ) {
    this.store.dispatch(
      SharedEmailProvidersActions.connectSharedMailbox({
        payload: provider,
      }),
    );

    const success$ = this.actions$.pipe(
      ofType(SharedEmailProvidersActions.updateOnePessimisticSuccess),
      take(1),
      tap(({ payload }) => ok(payload.data)),
    );

    const failed$ = this.actions$.pipe(
      ofType(SharedEmailProvidersActions.updateOnePessimisticFailed),
      take(1),
      tap(({ payload }) => fail()),
    );

    return race(success$, failed$);
  }

  public connect(provider: SharedEmailProvider) {
    this.sharedMailboxesAnalytics.trackConnectMailbox(provider.provider);
    switch (provider.provider) {
      case SharedEmailProviders.Google:
      case SharedEmailProviders.Microsoft:
        this.windowRef.open(provider.connectUrl!);
        break;
      case SharedEmailProviders.Mailbox:
        this.store.dispatch(
          SharedEmailProvidersActions.connectSharedMailbox({
            payload: provider,
          }),
        );
        break;
      default:
        throwUnsupportedProvider(<never>provider);
    }
  }
}
